import { useEffect, useRef, useState } from "react";
import {
	Copy,
	Download,
	ExternalLink,
	FileText,
	Loader2,
	Paperclip,
	Plus,
	Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApiSWR } from "@/hooks/useApiSWR";
import {
	type ChecklistItemAttachment,
	listChecklistItemAttachments,
	uploadChecklistItemAttachment,
	deleteChecklistItemAttachment,
} from "@/services/checklist-attachment.service";
import { type ChecklistItem } from "@/services/task-checklist.service";

const MAX_BYTES = 10 * 1024 * 1024;

const ALLOWED_MIME = new Set([
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/gif",
	"application/pdf",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/msword",
	"application/vnd.ms-excel",
	"text/plain",
	"application/zip",
	"application/x-zip-compressed",
]);

const ACCEPT_ATTR =
	".jpg,.jpeg,.png,.webp,.gif,.pdf,.docx,.xlsx,.doc,.xls,.txt,.zip";

function isImage(mime: string): boolean {
	return mime.startsWith("image/");
}

function isPdf(mime: string): boolean {
	return mime === "application/pdf";
}

function formatSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface Props {
	projectId: string;
	taskId: string;
	item: ChecklistItem;
	canEdit: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onChanged?: () => void;
}

export function ChecklistItemAttachmentsDialog({
	projectId,
	taskId,
	item,
	canEdit,
	open,
	onOpenChange,
	onChanged,
}: Props) {
	const {
		data,
		error: loadError,
		isLoading,
		mutate,
	} = useApiSWR<ChecklistItemAttachment[]>(
		open ? ["checklist-items", item.id, "attachments"] : null,
		() => listChecklistItemAttachments(projectId, taskId, item.id),
	);
	const items = data ?? [];
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [uploading, setUploading] = useState(false);
	const [deletingId, setDeletingId] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Keep a valid selection: default to the first attachment.
	useEffect(() => {
		if (items.length === 0) {
			setSelectedId(null);
			return;
		}
		if (!selectedId || !items.some((a) => a.id === selectedId)) {
			setSelectedId(items[0].id);
		}
	}, [items, selectedId]);

	const selected = items.find((a) => a.id === selectedId) ?? null;

	function openPicker() {
		inputRef.current?.click();
	}

	async function handleFiles(files: FileList | null) {
		if (!files || files.length === 0) return;

		const valid: File[] = [];
		for (const f of Array.from(files)) {
			if (!ALLOWED_MIME.has(f.type)) {
				toast.error("Unsupported file type", { description: f.name });
				continue;
			}
			if (f.size > MAX_BYTES) {
				toast.error("File too large", {
					description: `${f.name} exceeds 10 MB`,
				});
				continue;
			}
			valid.push(f);
		}

		if (valid.length === 0) return;

		setUploading(true);
		try {
			for (const file of valid) {
				try {
					const created = await uploadChecklistItemAttachment(
						projectId,
						taskId,
						item.id,
						file,
					);
					await mutate((curr) => [created, ...(curr ?? [])], {
						revalidate: false,
					});
					setSelectedId(created.id);
					toast.success("Attachment uploaded", {
						description: file.name,
					});
				} catch (e) {
					toast.error("Upload failed", {
						description: (e as Error).message || file.name,
					});
				}
			}
		} finally {
			setUploading(false);
			if (inputRef.current) inputRef.current.value = "";
			await mutate();
			onChanged?.();
		}
	}

	async function handleDelete(att: ChecklistItemAttachment) {
		if (!window.confirm(`Delete "${att.file_name}"?`)) return;
		setDeletingId(att.id);
		await mutate((curr) => (curr ?? []).filter((a) => a.id !== att.id), {
			revalidate: false,
		});
		try {
			await deleteChecklistItemAttachment(
				projectId,
				taskId,
				item.id,
				att.id,
			);
			toast.success("Attachment deleted");
		} catch (e) {
			toast.error("Delete failed", {
				description: (e as Error).message,
			});
		} finally {
			setDeletingId(null);
			await mutate();
			onChanged?.();
		}
	}

	async function handleCopyLink(url: string) {
		try {
			await navigator.clipboard.writeText(url);
			toast.success("Link copied");
		} catch {
			toast.error("Could not copy link");
		}
	}

	function handleDownload(att: ChecklistItemAttachment) {
		const a = document.createElement("a");
		a.href = att.file_url;
		a.download = att.file_name;
		a.rel = "noopener noreferrer";
		document.body.appendChild(a);
		a.click();
		a.remove();
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-4xl p-0 overflow-hidden">
				<DialogHeader className="border-b border-border bg-surface px-5 py-3">
					<DialogTitle className="flex items-center gap-2 text-sm">
						<Paperclip className="h-4 w-4 text-muted" />
						<span className="truncate">{item.title}</span>
						<span className="text-xs font-normal text-muted">
							({items.length})
						</span>
					</DialogTitle>
				</DialogHeader>

				<div className="grid grid-cols-1 md:grid-cols-[240px_1fr] h-[70vh] max-h-[70vh]">
					{/* Left: list + upload */}
					<div className="border-r border-border bg-muted-subtle/30 flex flex-col overflow-hidden">
						{canEdit && (
							<div className="p-3 border-b border-border">
								<input
									ref={inputRef}
									type="file"
									multiple
									accept={ACCEPT_ATTR}
									className="hidden"
									onChange={(e) => handleFiles(e.target.files)}
								/>
								<Button
									variant="outline"
									size="sm"
									className="w-full"
									onClick={openPicker}
									disabled={uploading}
								>
									{uploading ? (
										<Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
									) : (
										<Plus className="h-3.5 w-3.5 mr-1.5" />
									)}
									Add file
								</Button>
							</div>
						)}

						<div className="flex-1 overflow-y-auto p-2 space-y-1">
							{isLoading ? (
								<div className="flex items-center gap-2 text-xs text-muted px-2 py-3">
									<Loader2 className="h-3.5 w-3.5 animate-spin" />
									Loading...
								</div>
							) : loadError ? (
								<p className="text-xs text-danger px-2 py-3">
									Failed to load attachments.
								</p>
							) : items.length === 0 ? (
								<p className="text-xs text-muted text-center py-6">
									No attachments yet
								</p>
							) : (
								items.map((att) => (
									<AttachmentListRow
										key={att.id}
										attachment={att}
										active={att.id === selectedId}
										canDelete={canEdit}
										deleting={deletingId === att.id}
										onSelect={() => setSelectedId(att.id)}
										onDelete={() => handleDelete(att)}
									/>
								))
							)}
						</div>
					</div>

					{/* Right: preview + actions */}
					<div className="flex flex-col overflow-hidden">
						{selected ? (
							<>
								<div className="flex items-center justify-end gap-1.5 border-b border-border px-4 py-2">
									<Button
										variant="ghost"
										size="sm"
										onClick={() => handleDownload(selected)}
									>
										<Download className="h-3.5 w-3.5 mr-1.5" />
										Download
									</Button>
									<Button
										variant="ghost"
										size="sm"
										asChild
									>
										<a
											href={selected.file_url}
											target="_blank"
											rel="noopener noreferrer"
										>
											<ExternalLink className="h-3.5 w-3.5 mr-1.5" />
											Open
										</a>
									</Button>
									<Button
										variant="ghost"
										size="sm"
										onClick={() =>
											handleCopyLink(selected.file_url)
										}
									>
										<Copy className="h-3.5 w-3.5 mr-1.5" />
										Copy link
									</Button>
									{canEdit && (
										<Button
											variant="ghost"
											size="sm"
											className="text-danger hover:text-danger"
											onClick={() => handleDelete(selected)}
											disabled={deletingId === selected.id}
										>
											<Trash2 className="h-3.5 w-3.5 mr-1.5" />
											Delete
										</Button>
									)}
								</div>
								<div className="flex-1 overflow-auto bg-muted-subtle/20 flex items-center justify-center p-4">
									<AttachmentPreview attachment={selected} />
								</div>
							</>
						) : (
							<div className="flex-1 flex items-center justify-center text-xs text-muted">
								Select an attachment to preview
							</div>
						)}
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}

function AttachmentListRow({
	attachment,
	active,
	canDelete,
	deleting,
	onSelect,
	onDelete,
}: {
	attachment: ChecklistItemAttachment;
	active: boolean;
	canDelete: boolean;
	deleting: boolean;
	onSelect: () => void;
	onDelete: () => void;
}) {
	return (
		<div
			className={cn(
				"group flex items-center gap-2 rounded-md px-2 py-1.5 cursor-pointer transition-colors",
				active
					? "bg-primary/10 text-foreground"
					: "hover:bg-muted-subtle/60 text-foreground",
			)}
			onClick={onSelect}
		>
			<div className="h-8 w-8 shrink-0 rounded overflow-hidden border border-border bg-surface flex items-center justify-center">
				{isImage(attachment.mime_type) ? (
					<img
						src={attachment.file_url}
						alt={attachment.file_name}
						className="h-full w-full object-cover"
					/>
				) : (
					<FileText className="h-4 w-4 text-muted" />
				)}
			</div>
			<div className="min-w-0 flex-1">
				<p className="text-xs font-medium truncate">
					{attachment.file_name}
				</p>
				<p className="text-[10px] text-muted">
					{formatSize(attachment.file_size)}
				</p>
			</div>
			{canDelete && (
				<button
					type="button"
					onClick={(e) => {
						e.stopPropagation();
						onDelete();
					}}
					disabled={deleting}
					aria-label={`Delete ${attachment.file_name}`}
					className="opacity-0 group-hover:opacity-100 text-muted hover:text-danger transition-opacity shrink-0"
				>
					{deleting ? (
						<Loader2 className="h-3.5 w-3.5 animate-spin" />
					) : (
						<Trash2 className="h-3.5 w-3.5" />
					)}
				</button>
			)}
		</div>
	);
}

function AttachmentPreview({
	attachment,
}: {
	attachment: ChecklistItemAttachment;
}) {
	if (isImage(attachment.mime_type)) {
		return (
			<img
				src={attachment.file_url}
				alt={attachment.file_name}
				className="max-h-full max-w-full object-contain rounded"
			/>
		);
	}

	if (isPdf(attachment.mime_type)) {
		return (
			<iframe
				src={attachment.file_url}
				title={attachment.file_name}
				className="h-full w-full rounded border border-border bg-white"
			/>
		);
	}

	return (
		<div className="flex flex-col items-center justify-center text-center gap-2 text-muted">
			<FileText className="h-12 w-12" />
			<p className="text-sm font-medium text-foreground">
				{attachment.file_name}
			</p>
			<p className="text-xs">
				{formatSize(attachment.file_size)} · Preview not available
			</p>
			<a
				href={attachment.file_url}
				download={attachment.file_name}
				className="text-xs text-primary hover:underline"
			>
				Download to view
			</a>
		</div>
	);
}
